import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Component, HostListener, NgModule } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationStart, RouterModule } from '@angular/router';
import { __awaiter } from 'tslib';
import { Subject, merge, fromEvent, interval } from 'rxjs';
import { map, filter, first, delay, debounce } from 'rxjs/operators';

var TourState;
(function (TourState) {
    TourState[TourState["OFF"] = 0] = "OFF";
    TourState[TourState["ON"] = 1] = "ON";
    TourState[TourState["PAUSED"] = 2] = "PAUSED";
})(TourState || (TourState = {}));
var Direction;
(function (Direction) {
    Direction[Direction["Forwards"] = 0] = "Forwards";
    Direction[Direction["Backwards"] = 1] = "Backwards";
})(Direction || (Direction = {}));
// noinspection JSUnusedGlobalSymbols
class TourService {
    constructor(router, rendererFactory) {
        this.router = router;
        this.rendererFactory = rendererFactory;
        this.stepShow$ = new Subject();
        this.stepHide$ = new Subject();
        this.initialize$ = new Subject();
        this.start$ = new Subject();
        this.end$ = new Subject();
        this.pause$ = new Subject();
        this.resume$ = new Subject();
        this.anchorRegister$ = new Subject();
        this.anchorUnregister$ = new Subject();
        this.events$ = merge(this.stepShow$.pipe(map(value => ({ name: 'stepShow', value }))), this.stepHide$.pipe(map(value => ({ name: 'stepHide', value }))), this.initialize$.pipe(map(value => ({ name: 'initialize', value }))), this.start$.pipe(map(value => ({ name: 'start', value }))), this.end$.pipe(map(value => ({ name: 'end', value }))), this.pause$.pipe(map(value => ({ name: 'pause', value }))), this.resume$.pipe(map(value => ({ name: 'resume', value }))), this.anchorRegister$.pipe(map(value => ({
            name: 'anchorRegister',
            value
        }))), this.anchorUnregister$.pipe(map(value => ({
            name: 'anchorUnregister',
            value
        }))));
        this.steps = [];
        this.anchors = {};
        this.status = TourState.OFF;
        this.isHotKeysEnabled = true;
        this.direction = Direction.Forwards;
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    initialize(steps, stepDefaults) {
        if (steps && steps.length > 0) {
            this.status = TourState.OFF;
            this.steps = steps.map(step => Object.assign({}, stepDefaults, step));
            this.initialize$.next(this.steps);
        }
    }
    disableHotkeys() {
        this.isHotKeysEnabled = false;
    }
    enableHotkeys() {
        this.isHotKeysEnabled = true;
    }
    start() {
        this.startAt(0);
    }
    startAt(stepId) {
        this.status = TourState.ON;
        this.goToStep(this.loadStep(stepId));
        this.start$.next();
        this.router.events
            .pipe(filter(event => event instanceof NavigationStart), first())
            .subscribe(() => {
            if (this.currentStep && this.currentStep.hasOwnProperty('route')) {
                this.hideStep(this.currentStep);
            }
        });
    }
    end() {
        this.status = TourState.OFF;
        this.hideStep(this.currentStep);
        this.currentStep = undefined;
        this.removeLastAnchorClickListener();
        this.end$.next();
    }
    pause() {
        this.status = TourState.PAUSED;
        this.hideStep(this.currentStep);
        this.pause$.next();
    }
    resume() {
        this.status = TourState.ON;
        this.showStep(this.currentStep);
        this.resume$.next();
    }
    toggle(pause) {
        if (pause) {
            if (this.currentStep) {
                this.pause();
            }
            else {
                this.resume();
            }
        }
        else {
            if (this.currentStep) {
                this.end();
            }
            else {
                this.start();
            }
        }
    }
    next() {
        this.direction = Direction.Forwards;
        if (this.hasNext(this.currentStep)) {
            this.goToStep(this.loadStep(this.currentStep.nextStep || this.steps.indexOf(this.currentStep) + 1));
        }
    }
    hasNext(step) {
        if (!step) {
            console.warn('Can\'t get next step. No currentStep.');
            return false;
        }
        return (step.nextStep !== undefined ||
            (this.steps.indexOf(step) < this.steps.length - 1 && !this.isNextOptionalAnchorMissing(step)));
    }
    isNextOptionalAnchorMissing(step) {
        const stepIndex = this.steps.indexOf(step);
        for (let i = stepIndex + 1; i < this.steps.length; i++) {
            const nextStep = this.steps[i];
            if (!nextStep.isOptional || this.anchors[nextStep.anchorId])
                return false;
        }
        return true;
    }
    prev() {
        this.direction = Direction.Backwards;
        if (this.hasPrev(this.currentStep)) {
            this.goToStep(this.loadStep(this.currentStep.prevStep || this.steps.indexOf(this.currentStep) - 1));
        }
    }
    hasPrev(step) {
        if (!step) {
            console.warn('Can\'t get previous step. No currentStep.');
            return false;
        }
        return step.prevStep !== undefined ||
            (this.steps.indexOf(step) > 0 && !this.isPrevOptionalAnchorMising(step));
    }
    isPrevOptionalAnchorMising(step) {
        const stepIndex = this.steps.indexOf(step);
        for (let i = stepIndex - 1; i > -1; i--) {
            const prevStep = this.steps[i];
            if (!prevStep.isOptional || this.anchors[prevStep.anchorId])
                return false;
        }
        return true;
    }
    goto(stepId) {
        this.goToStep(this.loadStep(stepId));
    }
    register(anchorId, anchor) {
        if (!anchorId) {
            return;
        }
        if (this.anchors[anchorId]) {
            throw new Error('anchorId ' + anchorId + ' already registered!');
        }
        this.anchors[anchorId] = anchor;
        this.anchorRegister$.next(anchorId);
    }
    unregister(anchorId) {
        if (!anchorId) {
            return;
        }
        delete this.anchors[anchorId];
        this.anchorUnregister$.next(anchorId);
    }
    getStatus() {
        return this.status;
    }
    isHotkeysEnabled() {
        return this.isHotKeysEnabled;
    }
    goToStep(step) {
        if (!step) {
            console.warn('Can\'t go to non-existent step');
            this.end();
            return;
        }
        if (this.currentStep) {
            this.hideStep(this.currentStep);
        }
        this.removeLastAnchorClickListener();
        this.listenToOnAnchorClick(step);
        if (step.route !== undefined && step.route !== null) {
            this.navigateToRouteAndSetStep(step);
        }
        else {
            this.setCurrentStepAsync(step);
        }
    }
    removeLastAnchorClickListener() {
        if (this.unListenNextOnAnchorClickFn) {
            this.unListenNextOnAnchorClickFn();
            this.unListenNextOnAnchorClickFn = undefined;
        }
    }
    listenToOnAnchorClick(step) {
        if (step.goToNextOnAnchorClick) {
            const anchor = this.anchors[step.anchorId];
            this.unListenNextOnAnchorClickFn = this.renderer
                .listen(anchor.element.nativeElement, 'click', () => this.next());
        }
    }
    navigateToRouteAndSetStep(step) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = typeof step.route === 'string' ? step.route : this.router.createUrlTree(step.route), matchOptions = {
                paths: 'exact',
                matrixParams: 'exact',
                queryParams: 'subset',
                fragment: 'ignored'
            };
            const isActive = this.router.isActive(url, matchOptions);
            if (isActive) {
                this.setCurrentStepAsync(step);
                return;
            }
            const navigated = yield this.router.navigateByUrl(url);
            if (!navigated) {
                console.warn('Navigation to route failed: ', step.route);
                this.end();
            }
            else {
                this.setCurrentStepAsync(step, step.delayAfterNavigation);
            }
        });
    }
    loadStep(stepId) {
        if (typeof stepId === 'number') {
            return this.steps[stepId];
        }
        else {
            return this.steps.find(step => step.stepId === stepId);
        }
    }
    setCurrentStep(step) {
        this.currentStep = step;
        this.showStep(this.currentStep);
        this.router.events
            .pipe(filter(event => event instanceof NavigationStart), first())
            .subscribe(() => {
            if (this.currentStep && this.currentStep.hasOwnProperty('route')) {
                this.hideStep(this.currentStep);
            }
        });
    }
    setCurrentStepAsync(step, delay = 0) {
        setTimeout(() => this.setCurrentStep(step), delay);
    }
    showStep(step) {
        const anchor = this.anchors[step && step.anchorId];
        if (!anchor) {
            if (step.isAsync) {
                this.anchorRegister$
                    .pipe(filter(anchorId => anchorId === step.anchorId), first(), delay(0))
                    .subscribe(() => this.showStep(step));
                return;
            }
            if (step.isOptional) {
                this.direction === Direction.Forwards ? this.next() : this.prev();
                return;
            }
            console.warn('Can\'t attach to unregistered anchor with id ' + step.anchorId);
            this.end();
            return;
        }
        anchor.showTourStep(step);
        this.stepShow$.next(step);
    }
    hideStep(step) {
        const anchor = this.anchors[step && step.anchorId];
        if (!anchor) {
            return;
        }
        anchor.hideTourStep();
        this.stepHide$.next(step);
    }
}
TourService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourService, deps: [{ token: i1.Router }, { token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable });
TourService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i0.RendererFactory2 }]; } });

class TourHotkeyListenerComponent {
    constructor(tourService) {
        this.tourService = tourService;
    }
    /**
     * Configures hot keys for controlling the tour with the keyboard
     */
    onEscapeKey() {
        if (this.tourService.getStatus() === TourState.ON &&
            this.tourService.isHotkeysEnabled()) {
            this.tourService.end();
        }
    }
    onArrowRightKey() {
        if (this.tourService.getStatus() === TourState.ON &&
            this.tourService.hasNext(this.tourService.currentStep) &&
            this.tourService.isHotkeysEnabled()) {
            this.tourService.next();
        }
    }
    onArrowLeftKey() {
        if (this.tourService.getStatus() === TourState.ON &&
            this.tourService.hasPrev(this.tourService.currentStep) &&
            this.tourService.isHotkeysEnabled()) {
            this.tourService.prev();
        }
    }
}
TourHotkeyListenerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourHotkeyListenerComponent, deps: [{ token: TourService }], target: i0.ɵɵFactoryTarget.Component });
TourHotkeyListenerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: TourHotkeyListenerComponent, selector: "tour-hotkey-listener", host: { listeners: { "window:keydown.Escape": "onEscapeKey()", "window:keydown.ArrowRight": "onArrowRightKey()", "window:keydown.ArrowLeft": "onArrowLeftKey()" } }, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourHotkeyListenerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tour-hotkey-listener',
                    template: `<ng-content></ng-content>`
                }]
        }], ctorParameters: function () { return [{ type: TourService }]; }, propDecorators: { onEscapeKey: [{
                type: HostListener,
                args: ['window:keydown.Escape']
            }], onArrowRightKey: [{
                type: HostListener,
                args: ['window:keydown.ArrowRight']
            }], onArrowLeftKey: [{
                type: HostListener,
                args: ['window:keydown.ArrowLeft']
            }] } });

var ElementSides;
(function (ElementSides) {
    ElementSides[ElementSides["Top"] = 0] = "Top";
    ElementSides[ElementSides["Bottom"] = 1] = "Bottom";
    ElementSides[ElementSides["All"] = 2] = "All";
})(ElementSides || (ElementSides = {}));
function isInViewport(htlmElement, sidesToCheck = ElementSides.All) {
    const viewportWidth = window.innerWidth, viewportHeight = window.innerHeight, boundingRectangle = htlmElement.getBoundingClientRect(), areCornersInViewport = boundingRectangle.left >= 0 && boundingRectangle.right <= viewportWidth, isTopInViewport = boundingRectangle.top >= 0, isBottomInViewport = boundingRectangle.bottom <= viewportHeight;
    if (sidesToCheck === ElementSides.Top) {
        return isTopInViewport && areCornersInViewport;
    }
    if (sidesToCheck === ElementSides.Bottom) {
        return isBottomInViewport && areCornersInViewport;
    }
    return isTopInViewport && isBottomInViewport && areCornersInViewport;
}

class ScrollingUtil {
    static ensureVisible(htmlElement) {
        if (!isInViewport(htmlElement, ElementSides.Bottom)) {
            htmlElement.scrollIntoView(false);
        }
        else if (!isInViewport(htmlElement, ElementSides.Top)) {
            htmlElement.scrollIntoView(true);
        }
    }
}

class TourBackdropService {
    constructor(rendererFactory) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    show(targetElement, isScrollingEnabled = true) {
        this.isScrollingEnabled = isScrollingEnabled;
        this.targetHtmlElement = targetElement.nativeElement;
        if (!this.backdropElements) {
            this.backdropElements = this.createBackdropElements();
            this.subscribeToWindowResizeEvent();
        }
        this.setBackdropPosition();
    }
    setBackdropPosition() {
        const elementBoundingRect = this.targetHtmlElement.getBoundingClientRect(), docEl = document.documentElement, scrollHeight = docEl.scrollHeight, scrollWidth = docEl.scrollWidth, scrollX = window.scrollX, scrollY = window.scrollY, leftRect = {
            width: elementBoundingRect.left + scrollX,
            height: scrollHeight,
            top: 0,
            left: 0
        }, topRect = {
            width: elementBoundingRect.width,
            height: elementBoundingRect.top + scrollY,
            top: 0,
            left: elementBoundingRect.left + scrollX
        }, bottomRect = {
            width: elementBoundingRect.width,
            height: scrollHeight - (elementBoundingRect.bottom + scrollY),
            top: elementBoundingRect.bottom + scrollY,
            left: elementBoundingRect.left + scrollX
        }, rightRect = {
            width: scrollWidth - (elementBoundingRect.right + scrollX),
            height: scrollHeight,
            top: 0,
            left: elementBoundingRect.right + scrollX
        }, rectangles = [leftRect, topRect, bottomRect, rightRect];
        for (let i = 0; i < rectangles.length; i++) {
            const styles = this.createBackdropStyles(rectangles[i]);
            this.applyStyles(styles, this.backdropElements[i]);
        }
    }
    subscribeToWindowResizeEvent() {
        const resizeObservable$ = fromEvent(window, 'resize');
        this.windowResizeSubscription$ = resizeObservable$
            .pipe(debounce(() => interval(10)))
            .subscribe(() => {
            this.setBackdropPosition();
            ScrollingUtil.ensureVisible(this.targetHtmlElement);
        });
    }
    close() {
        if (this.backdropElements) {
            this.removeBackdropElement();
            this.windowResizeSubscription$.unsubscribe();
        }
    }
    removeBackdropElement() {
        this.backdropElements.forEach(backdropElement => this.renderer.removeChild(document.body, backdropElement));
        this.backdropElements = undefined;
    }
    applyStyles(styles, element) {
        for (const name of Object.keys(styles)) {
            this.renderer.setStyle(element, name, styles[name]);
        }
    }
    createBackdropStyles(rectangle) {
        return {
            position: this.isScrollingEnabled ? 'absolute' : 'fixed',
            width: `${rectangle.width}px`,
            height: `${rectangle.height}px`,
            top: `${rectangle.top}px`,
            left: `${rectangle.left}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: '101'
        };
    }
    createBackdropElement() {
        const backdropElement = this.renderer.createElement('div');
        this.renderer.addClass(backdropElement, 'ngx-ui-tour_backdrop');
        this.renderer.appendChild(document.body, backdropElement);
        return backdropElement;
    }
    createBackdropElements() {
        return Array
            .from({ length: 4 })
            .map(() => this.createBackdropElement());
    }
}
TourBackdropService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourBackdropService, deps: [{ token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable });
TourBackdropService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourBackdropService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourBackdropService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.RendererFactory2 }]; } });

class TourModule {
    static forRoot() {
        return {
            ngModule: TourModule,
            providers: [
                TourService,
                TourBackdropService
            ],
        };
    }
}
TourModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TourModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourModule, declarations: [TourHotkeyListenerComponent], imports: [CommonModule, RouterModule], exports: [TourHotkeyListenerComponent] });
TourModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourModule, imports: [[CommonModule, RouterModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TourModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [TourHotkeyListenerComponent],
                    exports: [TourHotkeyListenerComponent],
                    imports: [CommonModule, RouterModule],
                }]
        }] });

/*
 * Public API Surface of ngx-ui-tour-core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ElementSides, ScrollingUtil, TourBackdropService, TourHotkeyListenerComponent, TourModule, TourService, TourState, isInViewport };
